import { useCallback, useMemo, useEffect, useRef } from 'react';

import { validateWitnessCompanyRegistrationForm } from '../../utils/validations';
import {
  useWitnessCompanyFormData,
  useWitnessCompanyValidation,
  useWitnessCompanyActions,
  useWitnessCompanyTouchedFields,
} from '../../stores/company-registration.store';
import { useRepresentativeManagement } from '../common/use-representative-management';
import { useFieldValidation } from '../common/use-field-validation';
import { useDepartmentStaffs } from '../common/use-department-staffs';

import type { WitnessCompanyRegistrationForm } from '../../types/company.types';

import { PermissionAction } from '@/lib/enums';

export const useWitnessCompanyRegistrationForm = (
  onSuccess?: (response: WitnessCompanyRegistrationForm) => void,
  _onError?: (error: Error) => void,
  resetKey?: number
) => {
  // Get data and actions from global store
  const formData = useWitnessCompanyFormData();
  const validationState = useWitnessCompanyValidation();
  const touchedFields = useWitnessCompanyTouchedFields();
  const {
    updateForm,
    setValidation,
    resetForm: resetGlobalForm,
    setTouchedFields,
  } = useWitnessCompanyActions();

  // Common hooks for shared functionality
  const {
    departments,
    departmentsLoading,
    staffsLoading,
    getSalesPersonOptions,
    handleDepartmentChange,
  } = useDepartmentStaffs(PermissionAction.CREATE);

  // Reset form function
  const resetForm = useCallback(() => {
    resetGlobalForm();
    setTouchedFields(new Set());
    setValidation({
      errors: [],
      isValid: false,
      isSubmitting: false,
    });
    hasMarkedRequiredFields.current = false;
  }, [resetGlobalForm, setValidation, setTouchedFields]);

  // Reset form when resetKey changes
  useEffect(() => {
    if (resetKey !== undefined && resetKey > 0) {
      resetForm();
    }
  }, [resetKey, resetForm]);

  // Field update functions
  const updateField = useCallback(
    (field: keyof WitnessCompanyRegistrationForm, value: string | boolean) => {
      updateForm({ [field]: value });
      setTouchedFields(new Set(touchedFields).add(field));
    },
    [updateForm, setTouchedFields, touchedFields]
  );

  // Representative management using common hook
  const { addRepresentative, updateRepresentative, removeRepresentative, canAddRepresentative } =
    useRepresentativeManagement({
      representatives: formData.representatives,
      updateForm,
      touchedFields,
      setTouchedFields,
      maxRepresentatives: 10,
    });

  // Validate form whenever formData changes
  const validation = useMemo(() => {
    return validateWitnessCompanyRegistrationForm(formData);
  }, [formData]);

  // Mark all required fields as touched on form load to show required errors immediately
  const hasMarkedRequiredFields = useRef(false);

  useEffect(() => {
    // Only run once when component mounts or when representatives change
    if (hasMarkedRequiredFields.current) return;

    // Always preserve existing touched fields and ensure required fields are marked
    const newTouchedFields = new Set(touchedFields);

    // Mark basic required fields as touched
    newTouchedFields.add('officialName');
    newTouchedFields.add('tel');
    newTouchedFields.add('fax');

    // Mark representative fields as touched
    formData.representatives.forEach((_, index) => {
      const deptField = `representatives.${index}.department`;
      const salesField = `representatives.${index}.salesPerson`;

      newTouchedFields.add(deptField);
      newTouchedFields.add(salesField);
    });

    // Only update if there are new fields to add
    if (
      newTouchedFields.size !== touchedFields.size ||
      ![...newTouchedFields].every((field) => touchedFields.has(field))
    ) {
      setTouchedFields(newTouchedFields);
      hasMarkedRequiredFields.current = true;
    }
  }, [formData.representatives, setTouchedFields, touchedFields]);

  // Field validation helpers using common hook
  const { getFieldError, isFieldValid } = useFieldValidation({
    errors: validation.errors,
    touchedFields,
  });

  // Form submission
  const handleSubmit = useCallback(async (): Promise<boolean> => {
    if (!validation.isValid) {
      console.warn('Form validation failed, cannot submit');
      return false;
    }

    setValidation({ ...validationState, isSubmitting: true });

    try {
      // Call API directly like Construction form
      if (onSuccess) {
        await Promise.resolve(onSuccess(formData));
      }
      return true;
    } catch (error) {
      console.error('Witness Company registration failed:', error);
      setValidation({
        ...validationState,
        errors: [{ field: 'general', message: '登録に失敗しました。もう一度お試しください。' }],
        isValid: false,
      });
      return false;
    } finally {
      setValidation({ ...validationState, isSubmitting: false });
    }
  }, [formData, validation, validationState, onSuccess, setValidation]);

  return {
    formData,
    validationState: {
      errors: validation.errors,
      isValid: validation.isValid,
      isSubmitting: validationState.isSubmitting,
    },
    updateField,
    addRepresentative,
    updateRepresentative,
    removeRepresentative,
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,
    canAddRepresentative,
    isSubmitDisabled: !validation.isValid || validationState.isSubmitting,
    handleSubmit,
    resetForm,
    // Department and staffs properties
    departments,
    departmentsLoading,
    staffsLoading,
    handleDepartmentChange,
  };
};
